// src/components/Inquiries/Inquiries.js
import React, { useEffect, useState, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../services/api/axios";
import InquiriesTable from "./InquiriesTable";
import SearchBar from "../../../shared/components/SearchBar";
import useRowSelection from "../../../hooks/useRowSelection";
import { downloadCsvFile, filterInquiries } from "./InquiriesHelpers";
import BrandPagination from "../../../layouts/Pagination";
import { formatLocationLabel } from "../companies/ProspectingCompaniesHelpers";
import {
  normalizeCountry,
  normalizeState,
  normalizeString,
} from "../../../utils/filterNormalization";

import EntityProfile from "../../../shared/components/EntityProfile";
import DeleteConfirmDialog from "../../../shared/components/DeleteConfirmDialog";
import { inquiryProfileConfig } from "../../../shared/profile-configs/InquiryProfileConfig";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AddIcon from "@mui/icons-material/Add";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DownloadIcon from "@mui/icons-material/Download";

import {
  Box,
  Button,
  Card,
  Chip,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import {
  ACTION_MENU_ICON_SX,
  ACTION_MENU_ICON_TONES,
  ACTION_MENU_PAPER_SX,
  ACTIONS_HEADER_BTN_SX,
} from "../../../theme/buttonstyles";

const BRAND_BLUE = "#00336C";
const PAGE_BG = "#f7f9fc";

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  background: "#ffffff",
  overflow: "hidden",
};

function Inquiries() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [inquiries, setInquiries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    city: "",
    state_province: "",
    country: "",
  });
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const [profileOpen, setProfileOpen] = useState(false);
  const [activeInquiry, setActiveInquiry] = useState(null);

  const {
    selectedIds,
    selectedCount,
    isSelected,
    toggleOne,
    toggleMany,
    clearSelection,
  } = useRowSelection();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const recentDaysParam = searchParams.get("recent_days");
  const recentDays = Number.parseInt(recentDaysParam || "", 10);
  const hasRecentDaysFilter = Number.isInteger(recentDays) && recentDays > 0;
  const actionsMenuOpen = Boolean(actionsAnchorEl);

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarSeverity(severity);
    setSnackbarMsg(message);
    setSnackbarOpen(true);
  }, []);

  const fetchInquiries = useCallback(async () => {
    try {
      const res = await api.get("/inquiries", {
        params: {
          search: searchTerm || undefined,
        },
      });
      setInquiries(res.data);
      return res.data;
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to load inquiries.", "error");
      return [];
    }
  }, [searchTerm, showSnackbar]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, recentDaysParam, filters]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleLocationFilterChange = useCallback((nextLocation) => {
    setFilters((prev) => ({
      ...prev,
      city: nextLocation.city || "",
      state_province: nextLocation.state_province || "",
      country: nextLocation.country || "",
    }));
  }, []);

  /* ================= STATUS UPDATE (🔥 FIX) ================= */

  const handleUpdateStatus = async (id, status) => {
    const previousInquiries = inquiries;

    try {
      setInquiries((prev) =>
        prev.map((i) => (i.id === id ? { ...i, status } : i)),
      );

      await api.put(`/inquiries/${id}`, { status });
    } catch (err) {
      console.error(err);
      setInquiries(previousInquiries);
      showSnackbar("Failed to update status.", "error");
    }
  };

  /* ---------------- Convert ---------------- */

  const convertSelected = async () => {
    const ids = Array.from(selectedIds);

    if (ids.length === 0) {
      showSnackbar("No inquiries selected.", "info");
      return;
    }

    try {
      await Promise.all(ids.map((id) => api.post(`/inquiries/${id}/convert`)));

      showSnackbar(`${ids.length} inquiries converted.`);
      clearSelection();
      await fetchInquiries();
    } catch {
      showSnackbar("Conversion failed.", "error");
    }
  };

  /* ---------------- Delete ---------------- */

  const deleteSelected = () => {
    if (selectedIds.size === 0) {
      showSnackbar("No inquiries selected.", "info");
      return;
    }

    setDeleteConfirmOpen(true);
  };

  const confirmDeleteSelected = async () => {
    const ids = Array.from(selectedIds);

    setDeleting(true);
    try {
      await Promise.all(ids.map((id) => api.delete(`/inquiries/${id}`)));

      showSnackbar(`${ids.length} inquiries deleted.`);
      clearSelection();
      await fetchInquiries();
      setDeleteConfirmOpen(false);
    } catch {
      showSnackbar("Delete failed.", "error");
    } finally {
      setDeleting(false);
    }
  };

  /* ---------------- Export ---------------- */

  const exportCSV = () => {
    let rows;

    if (selectedCount > 0) {
      rows = inquiries.filter((i) => selectedIds.has(String(i.id)));
    } else {
      rows = filtered;
    }

    if (rows.length === 0) {
      showSnackbar("No data to export.", "info");
      return;
    }

    const headers = [
      "Company",
      "Contact",
      "Email",
      "Phone",
      "Sector",
      "NAICS",
      "Status",
    ];

    const csvRows = rows.map((i) => [
      i.company_name,
      i.contact_name,
      i.email,
      i.phone,
      i.sector,
      i.naics_category,
      i.status,
    ]);

    const csvContent = [headers, ...csvRows]
      .map((row) => row.map((v) => `"${v ?? ""}"`).join(","))
      .join("\n");

    downloadCsvFile(csvContent, "inquiries_export.csv");
    showSnackbar("Export complete.");
  };

  /* ---------------- Filtering ---------------- */

  const filtered = useMemo(() => {
    let nextFiltered = filterInquiries(inquiries, searchTerm);

    if (filters.status) {
      nextFiltered = nextFiltered.filter((inquiry) => inquiry.status === filters.status);
    }

    if (filters.city || filters.state_province || filters.country) {
      const filterCity = normalizeString(filters.city);
      const filterState = normalizeState(filters.state_province);
      const filterCountry = normalizeCountry(filters.country);

      nextFiltered = nextFiltered.filter((inquiry) => {
        const inquiryCity = normalizeString(inquiry.city);
        const inquiryState = normalizeState(inquiry.state_province);
        const inquiryCountry = normalizeCountry(inquiry.country);

        return (
          inquiryCity === filterCity &&
          inquiryState === filterState &&
          inquiryCountry === filterCountry
        );
      });
    }

    if (!hasRecentDaysFilter) {
      return nextFiltered;
    }

    const cutoff = dayjs().subtract(recentDays, "day");

    return nextFiltered.filter((inquiry) => {
      if (!inquiry.created_at) return false;
      const createdAt = dayjs(inquiry.created_at);
      return createdAt.isValid() && !createdAt.isBefore(cutoff);
    });
  }, [filters, hasRecentDaysFilter, inquiries, recentDays, searchTerm]);

  const locationOptions = useMemo(
    () =>
      Array.from(
        new Map(
          inquiries
            .map((inquiry) => {
              const city = normalizeString(inquiry?.city);
              const stateProvince = normalizeState(inquiry?.state_province);
              const country = normalizeCountry(inquiry?.country);
              const label = formatLocationLabel({
                city,
                state: stateProvince,
                country,
              });

              if (label === "—") {
                return null;
              }

              const key = [city, stateProvince, country].join("|");
              return [
                key,
                {
                  key,
                  label,
                  city,
                  state_province: stateProvince,
                  country,
                },
              ];
            })
            .filter(Boolean),
        ).values(),
      ).sort((a, b) => a.label.localeCompare(b.label)),
    [inquiries],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);

  const paginated = filtered.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );

  const currentPageIds = paginated.map((i) => String(i.id));

  const allCurrentSelected =
    currentPageIds.length > 0 && currentPageIds.every((id) => isSelected(id));

  const someCurrentSelected = currentPageIds.some((id) => isSelected(id));

  const handleOpenActionsMenu = (event) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const handleCloseActionsMenu = () => {
    setActionsAnchorEl(null);
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: PAGE_BG,
        pb: 4,
      }}
    >
      {/* ===== Sticky Header ===== */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
            flexWrap: "wrap",
          }}
        >
          {/* Left: icon + title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: "#648315",
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 19 }} />
            </Box>
            <Box
              sx={{
                fontSize: 21,
                fontWeight: 800,
                color: BRAND_BLUE,
                lineHeight: 1.05,
                whiteSpace: "nowrap",
              }}
            >
              Inquiries
            </Box>
          </Box>

          {/* Center: search */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <SearchBar
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v);
                setPage(1);
              }}
              placeholder="Search Inquiries by Company or Contact..."
              maxWidth={1700}
            />
          </Box>

          {/* Right: actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleOpenActionsMenu}
              sx={ACTIONS_HEADER_BTN_SX}
            >
              Actions
            </Button>

            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsMenuOpen}
              onClose={handleCloseActionsMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
            >
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  navigate("/inquiries/new");
                }}
              >
                <ListItemIcon
                  sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.add }}
                >
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Inquiry</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  convertSelected();
                }}
                disabled={selectedCount === 0}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.insight,
                  }}
                >
                  <TrendingUpIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Convert to Prospect Company" />
                <ArrowForwardIcon sx={{ ml: 1, fontSize: 18 }} />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  exportCSV();
                }}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.export,
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  clearSelection();
                }}
                disabled={selectedCount === 0}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.clear,
                  }}
                >
                  <ClearAllIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Clear Selection</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  deleteSelected();
                }}
                disabled={selectedCount === 0}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.delete,
                  }}
                >
                  <DeleteForeverIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete Selected</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* ===== Content Area ===== */}
      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        {hasRecentDaysFilter && (
          <Box sx={{ mb: 2 }}>
            <Chip
              color="primary"
              variant="outlined"
              label={`Showing new inquiries from the last ${recentDays} days`}
              onDelete={() => {
                const nextParams = new URLSearchParams(searchParams);
                nextParams.delete("recent_days");
                setSearchParams(nextParams);
              }}
            />
          </Box>
        )}

        <Card sx={CARD_SX}>
          <InquiriesTable
            paginated={paginated}
            isSelected={isSelected}
            toggleOne={toggleOne}
            toggleMany={toggleMany}
            allCurrentSelected={allCurrentSelected}
            someCurrentSelected={someCurrentSelected}
            navigate={navigate}
            setActiveInquiry={setActiveInquiry}
            setProfileOpen={setProfileOpen}
            handleUpdateStatus={handleUpdateStatus}
            filters={filters}
            onFilterChange={handleFilterChange}
            onLocationFilterChange={handleLocationFilterChange}
            locationOptions={locationOptions}
          />
        </Card>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <BrandPagination
            page={safePage}
            count={totalPages || Math.ceil(filtered.length / pageSize) || 1}
            onChange={(_, value) => setPage(value)}
          />
        </Box>
      </Box>

      <EntityProfile
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        entity={activeInquiry}
        sections={activeInquiry ? inquiryProfileConfig(activeInquiry) : []}
        dialogPaperSx={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "#00336C",
          border: "1px solid #00336C",
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity={snackbarSeverity}>{snackbarMsg}</Alert>
      </Snackbar>

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteSelected}
        loading={deleting}
        title="Delete Inquiries"
        description={`Are you sure you want to delete ${selectedIds.size} inquir${selectedIds.size === 1 ? "y" : "ies"}? This action can't be undone.`}
      />
    </Box>
  );
}

export default Inquiries;
